// Care Library domain types + presentation config.
//
// The catalog itself — the offerings and the category axes — is sourced from
// Medplum at runtime (see `care-library-fhir.ts`, which maps FHIR
// ActivityDefinitions and ValueSets into the `Org` shape below). What lives
// here is the *contract* the browse/detail components render against, plus the
// per-type presentation config (labels, kinds, short names) that is UI styling
// rather than catalog data — the visual styling lives alongside it in
// `offering-types.tsx`.
//
// The thesis (see the design brief): the "library" is a CATALOG OF SERVICES.
// Exercise is one TYPE (axis 1). Every org groups its offerings by its OWN
// CATEGORIES (axis 2). Each type carries different PARAMETERS (axis 3). All
// three axes are properties of one underlying reusable activity definition.

/** Axis 1 — what kind of offering this is. Drives icon, color, and which
 *  parameters show. */
export type OfferingType =
  | 'exercise'
  | 'education'
  | 'treatment'
  | 'consult'
  | 'group'
  | 'visit'
  | 'referral'
  | 'message';

export interface OfferingTypeMeta {
  /** Full label, e.g. "Patient education". */
  label: string;
  /** Who performs it: self-management / clinician-delivered / coordination. */
  kind: string;
  /** Short chip label, e.g. "Education". */
  short: string;
}

/** Order the types appear in the type-filter bar. */
export const TYPE_ORDER: OfferingType[] = [
  'exercise',
  'education',
  'treatment',
  'consult',
  'group',
  'visit',
  'referral',
  'message',
];

export const TYPE_META: Record<OfferingType, OfferingTypeMeta> = {
  exercise: { label: 'Exercise', kind: 'Self-management', short: 'Exercise' },
  education: {
    label: 'Patient education',
    kind: 'Self-management',
    short: 'Education',
  },
  treatment: {
    label: 'In-clinic treatment',
    kind: 'Clinician-delivered',
    short: 'Treatment',
  },
  consult: { label: 'Consult', kind: 'Clinician-delivered', short: 'Consult' },
  group: { label: 'Group class', kind: 'Clinician-delivered', short: 'Group' },
  visit: {
    label: 'Follow-up / visit',
    kind: 'Clinician-delivered',
    short: 'Visit',
  },
  referral: { label: 'Referral', kind: 'Coordination', short: 'Referral' },
  message: {
    label: 'Messaging touchpoint',
    kind: 'Self-management',
    short: 'Message',
  },
};

/** A type-scoped display parameter (axis 3). `mono` renders the numeric face. */
export interface OfferingParam {
  label: string;
  value: string;
  mono?: boolean;
}

/** Axis 2 — a single category within one of the org's grouping axes. */
export interface FacetCategory {
  id: string;
  name: string;
  /** How many offerings sit in this category — derived from the catalog. */
  count: number;
}

/** Axis 2 — one grouping axis the org defines (service line, body region, …). */
export interface Facet {
  id: string;
  name: string;
  /** The org's main grouping axis; surfaces first and drives card chips. */
  primary?: boolean;
  cats: FacetCategory[];
}

export interface Offering {
  id: string;
  type: OfferingType;
  name: string;
  /** Optional clinical code (SNOMED/CPT), pre-formatted for display. */
  code: string | null;
  /** Type-specific, prescribe-time parameter line (axis 3). Display only. */
  meta: string;
  /** Category memberships keyed by facet id; an item can sit in several. */
  cats: Record<string, string[]>;
  desc: string;
  /** Type-scoped parameter grid (axis 3) for the detail view; empty when the
   *  offering carries no structured parameters. */
  params: OfferingParam[];
  /** Patient-facing instructions for the detail view, when authored. */
  instructions: string | null;
}

export interface Org {
  id: string;
  name: string;
  facets: Facet[];
  items: Offering[];
}

/** Look up a single offering by id. */
export const findOffering = (org: Org, id: string): Offering | undefined =>
  org.items.find((it) => it.id === id);

/** Tally how many offerings carry each type — for the type-filter counts. */
export const typeCounts = (org: Org): Partial<Record<OfferingType, number>> => {
  const counts: Partial<Record<OfferingType, number>> = {};
  for (const item of org.items) {
    counts[item.type] = (counts[item.type] ?? 0) + 1;
  }
  return counts;
};

/** Resolve a category id to its display name within a facet. */
export const categoryName = (
  org: Org,
  facetId: string,
  catId: string,
): string => {
  const facet = org.facets.find((f) => f.id === facetId);
  const cat = facet?.cats.find((c) => c.id === catId);
  return cat?.name ?? catId;
};
